/**
 * Editor-local model for dynamic content slots: an element in the email whose
 * content varies by audience branch. Placement id matches the HTML marker /
 * usage id; branches keep the shape returned by `contentEmailDynamicSlots`.
 *
 * The channel-neutral domain core (branch/slot/variant types and helpers)
 * lives in the shared `src/pages/lifecycle-studio/dynamic-content/slot-types`
 * module and is re-exported here for the email editor's consumers; this file
 * keeps the email wire parser and the DOM/HTML helpers.
 */

import { normalizeAudienceConditionOperators } from "@hightouch/lib/llm/lifecycle-studio/dynamic-content/types";
import type { RootCondition } from "src/types/visual";

import type {
  DynamicSlot,
  SlotBranch,
} from "src/pages/lifecycle-studio/dynamic-content/slot-types";

// The contract (marker attribute, self-closing tag set, root-tag helpers) is
// shared with the backend — one source of truth for both sides.
export {
  DYNAMIC_SLOT_ATTRIBUTE,
  contentRootTagName,
} from "@hightouch/lib/llm/lifecycle-studio/dynamic-content/shared";
import { DYNAMIC_SLOT_ATTRIBUTE } from "@hightouch/lib/llm/lifecycle-studio/dynamic-content/shared";

export {
  branchEspCondition,
  branchHasLogic,
  branchVariantId,
  createConditionalBranch,
  createFallbackBranch,
  isEmptyCondition,
  isFallbackBranch,
  nextDefaultSlotName,
  seedNewVariant,
  slotVariants,
  sortBranches,
  stableVariantId,
  summarizeBranchLogic,
  summarizeCondition,
} from "src/pages/lifecycle-studio/dynamic-content/slot-types";
export type {
  AssignmentSource,
  DynamicSlot,
  SlotAssignmentType,
  SlotBranch,
  SlotVariant,
  VariantOption,
} from "src/pages/lifecycle-studio/dynamic-content/slot-types";

export function getSlotId(element: Element | null): string | null {
  return element?.getAttribute(DYNAMIC_SLOT_ATTRIBUTE) ?? null;
}

export function findSlotElement(doc: Document, slotId: string): Element | null {
  return doc.querySelector(
    `[${DYNAMIC_SLOT_ATTRIBUTE}="${CSS.escape(slotId)}"]`,
  );
}

/**
 * Remove editor-injected artifacts from captured element HTML before storing
 * or comparing it: `contenteditable`, comment/path markers, and selection
 * highlight classes can be present on descendants when content was captured
 * mid-edit. Regex-based (no DOM re-parse) so table fragments (`<tr>`/`<td>`
 * innerHTML) survive intact.
 */
export function stripEditorArtifacts(html: string): string {
  return html
    .replace(/ contenteditable="(true|false|)"/gi, "")
    .replace(/ data-ht-path="[^"]*"/g, "")
    .replace(/ data-ht-comment-id="[^"]*"/g, "")
    .replace(/\s?class="([^"]*)"/g, (_match, classes: string) => {
      const kept = classes
        .split(/\s+/)
        .filter(
          (c) => c && c !== "ht-selected-element" && c !== "ht-hover-element",
        );
      return kept.length ? ` class="${kept.join(" ")}"` : "";
    });
}

/**
 * Narrow a slot row from `contentEmailDynamicSlots` (loose jsonb branches)
 * into a DynamicSlot at the boundary. Returns null for malformed rows so one
 * bad row can't break the editor.
 */
export function parseSlotRow(row: {
  id: string;
  name?: string | null;
  assignmentType?: string | null;
  assignmentSource?: string | null;
  branches: unknown;
}): DynamicSlot | null {
  if (!row.id || !Array.isArray(row.branches)) return null;

  const branches: SlotBranch[] = [];
  for (const raw of row.branches) {
    if (!raw || typeof raw !== "object") return null;
    const b = raw as Record<string, unknown>;
    if (typeof b.id !== "string" || typeof b.name !== "string") return null;
    if (typeof b.content !== "string") return null;

    let condition: RootCondition | null = null;
    if (b.condition !== null && b.condition !== undefined) {
      const c = b.condition as Record<string, unknown>;
      // Conditions are audience ASTs; anything else is malformed.
      if (typeof c.type !== "string") return null;
      // Remap SQL-ish null checks (`is_not_null`) to visual-query operators
      // (`exists`) so the Filter chip / operator Select aren't stuck on
      // "Filter on".
      condition = normalizeAudienceConditionOperators(
        b.condition as RootCondition,
      );
    }
    // esp_profile branches carry their predicate here instead of an audience
    // AST; anything non-string is malformed.
    if (b.espCondition != null && typeof b.espCondition !== "string") {
      return null;
    }
    // The branch's content row id — writes send it back as a reference so
    // the row (and any N:1 share) is kept. Anything non-string is malformed.
    if (b.contentId != null && typeof b.contentId !== "string") {
      return null;
    }
    branches.push({
      id: b.id,
      name: b.name,
      condition,
      ...(typeof b.espCondition === "string"
        ? { espCondition: b.espCondition }
        : {}),
      content: b.content,
      ...(typeof b.contentId === "string" ? { contentId: b.contentId } : {}),
    });
  }

  // No fallback requirement: a null-condition ("everyone else") branch is a
  // deploy-time concern (readiness/compile), not a validity concern — drafts
  // may have every variant unassigned.

  const name = row.name?.trim() || "Dynamic content";

  // Only "conditional" is supported today; reject unknown values so we don't
  // smuggle a free string into SlotAssignmentType.
  if (row.assignmentType != null && row.assignmentType !== "conditional") {
    return null;
  }

  // Same guard for the condition mode: an unrecognized mode would render the
  // wrong conditions editor and mangle branches on save.
  if (
    row.assignmentSource !== "audience_schema" &&
    row.assignmentSource !== "esp_profile"
  ) {
    return null;
  }

  return {
    id: row.id,
    name,
    assignmentType: "conditional",
    assignmentSource: row.assignmentSource,
    // Stored order is preserved: it's insertion order for manual edits and
    // priority order once the conditions editor has arranged it. The fallback
    // is identified by its null condition, never by position.
    branches,
  };
}
